'use client';

// ============================================================================
// WorkItemCustomerPricingPanel — per-customer contracted prices for a product.
// Backed by /api/v1/customer-items via useWorkItemCustomerItems.
// Date: April 22, 2026
// ============================================================================

import React, { useCallback, useMemo, useState } from 'react';
import { Plus, Trash2, X, Star, Save } from 'lucide-react';
import { useWorkItemCustomerItems, type CustomerItemCreatePayload } from '@/hooks/useWorkItemCustomerItems';
import { notify, confirm } from '@/lib/notifications';

interface CustomerOption {
  id: number;
  name: string;
  currency?: string | null;
}

interface Props {
  workItemId: string;
  productName?: string;
  baseSellingPrice?: number | null;
  baseCurrency?: string;
  customerContacts: CustomerOption[];
}

const formatMoney = (value: number | string | null | undefined, currency: string) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${currency} ${n.toFixed(2)}`;
};

const getErrorMessage = (error: unknown, fallback: string) => {
  return error instanceof Error ? error.message : fallback;
};

export default function WorkItemCustomerPricingPanel({
  workItemId,
  productName,
  baseSellingPrice,
  baseCurrency = 'ZAR',
  customerContacts,
}: Props) {
  const { items, loading, error, create, update, remove } = useWorkItemCustomerItems(workItemId);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ contracted_price: string; min_order_qty: string }>({
    contracted_price: '',
    min_order_qty: '',
  });

  const initialFormState: CustomerItemCreatePayload & { _contracted_price_str: string } = useMemo(
    () => ({
      contact_id: 0,
      work_item_id: workItemId,
      contracted_price: null,
      contract_currency: baseCurrency,
      customer_sku: '',
      customer_description: '',
      contract_start: null,
      contract_end: null,
      min_order_qty: null,
      lead_time_days: null,
      is_preferred: false,
      is_active: true,
      notes: '',
      _contracted_price_str: '',
    }),
    [workItemId, baseCurrency],
  );

  const [form, setForm] = useState(initialFormState);

  // Already-mapped contact IDs so we don't offer them in the picker.
  const usedContactIds = useMemo(() => new Set(items.map((it) => it.contact_id)), [items]);
  const availableContacts = useMemo(
    () => customerContacts.filter((c) => !usedContactIds.has(c.id)),
    [customerContacts, usedContactIds],
  );

  const baseRef = Number(baseSellingPrice);
  const baseRefValid = Number.isFinite(baseRef) && baseRef > 0;

  const computeDelta = useCallback((price: number | null | undefined): { pct: number | null; better: boolean } => {
    if (!baseRefValid) return { pct: null, better: false };
    const p = Number(price);
    if (!Number.isFinite(p) || p <= 0) return { pct: null, better: false };
    const pct = ((p - baseRef) / baseRef) * 100;
    return { pct, better: pct < 0 };
  }, [baseRef, baseRefValid]);

  const activeContracts = useMemo(() => items.filter((item) => item.is_active), [items]);
  const preferredContracts = useMemo(
    () => activeContracts.filter((item) => item.is_preferred),
    [activeContracts],
  );
  const strongestDelta = useMemo(() => {
    return activeContracts.reduce<null | { contactName: string; pct: number }>((best, item) => {
      const delta = computeDelta(item.contracted_price);
      if (delta.pct == null) {
        return best;
      }

      if (!best || delta.pct < best.pct) {
        return {
          contactName: item.contact_name || `Contact #${item.contact_id}`,
          pct: delta.pct,
        };
      }

      return best;
    }, null);
  }, [activeContracts, computeDelta]);

  const resetForm = () => {
    setForm(initialFormState);
    setShowForm(false);
    setFormError(null);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!form.contact_id) {
      setFormError('Pick a customer.');
      return;
    }
    setSubmitting(true);
    try {
      const priceNum = form._contracted_price_str.trim() === '' ? null : Number(form._contracted_price_str);
      await create({
        contact_id: form.contact_id,
        work_item_id: workItemId,
        customer_sku: form.customer_sku || null,
        customer_description: form.customer_description || null,
        contracted_price: priceNum,
        contract_currency: form.contract_currency || baseCurrency,
        contract_start: form.contract_start || null,
        contract_end: form.contract_end || null,
        min_order_qty: form.min_order_qty ?? null,
        lead_time_days: form.lead_time_days ?? null,
        is_preferred: !!form.is_preferred,
        is_active: form.is_active !== false,
        notes: form.notes || null,
      });
      resetForm();
    } catch (err: unknown) {
      setFormError(getErrorMessage(err, 'Failed to add customer pricing.'));
    } finally {
      setSubmitting(false);
    }
  };

  const beginEdit = (id: string, contractedPrice: number | string | null, minQty: number | string | null) => {
    setEditingId(id);
    setDraft({
      contracted_price: contractedPrice == null ? '' : String(contractedPrice),
      min_order_qty: minQty == null ? '' : String(minQty),
    });
  };

  const commitEdit = async (id: string) => {
    try {
      const priceNum = draft.contracted_price.trim() === '' ? null : Number(draft.contracted_price);
      const qtyNum = draft.min_order_qty.trim() === '' ? null : Number(draft.min_order_qty);
      await update(id, { contracted_price: priceNum, min_order_qty: qtyNum });
      setEditingId(null);
    } catch (err: unknown) {
      notify.error(getErrorMessage(err, 'Failed to update customer pricing.'));
    }
  };

  const toggleActive = async (id: string, current: boolean) => {
    try {
      await update(id, { is_active: !current });
    } catch (err: unknown) {
      notify.error(getErrorMessage(err, 'Failed to update.'));
    }
  };

  const togglePreferred = async (id: string, current: boolean) => {
    try {
      await update(id, { is_preferred: !current });
    } catch (err: unknown) {
      notify.error(getErrorMessage(err, 'Failed to update preferred status.'));
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Customer Pricing{productName ? ` — ${productName}` : ''}
          </h3>
          {baseRefValid && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Base selling price: <span className="font-mono">{formatMoney(baseRef, baseCurrency)}</span>
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          disabled={availableContacts.length === 0}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded inline-flex items-center gap-1 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          title={availableContacts.length === 0 ? 'All customers already mapped' : 'Add customer pricing'}
        >
          <Plus className="w-4 h-4" /> Add Customer
        </button>
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-3">
        <div className="rounded-xl border border-blue-200 bg-blue-50/70 p-3 dark:border-blue-900/40 dark:bg-blue-950/20">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-700 dark:text-blue-300">Resolver order</p>
          <p className="mt-1 text-sm font-medium text-blue-900 dark:text-blue-100">Customer contract → price list → base selling price</p>
          <p className="mt-1 text-xs text-blue-800/80 dark:text-blue-200/80">These prices win first on quotes, sales orders, and invoices for the mapped customer.</p>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/20">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">Coverage</p>
          <p className="mt-1 text-sm font-medium text-emerald-900 dark:text-emerald-100">{activeContracts.length} active contract{activeContracts.length === 1 ? '' : 's'}</p>
          <p className="mt-1 text-xs text-emerald-800/80 dark:text-emerald-200/80">{preferredContracts.length} marked preferred for rapid review and pricing audits.</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3 dark:border-amber-900/40 dark:bg-amber-950/20">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-300">Best delta vs base</p>
          <p className="mt-1 text-sm font-medium text-amber-900 dark:text-amber-100">
            {strongestDelta ? `${strongestDelta.pct > 0 ? '+' : ''}${strongestDelta.pct.toFixed(1)}%` : 'No priced contracts yet'}
          </p>
          <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-200/80">
            {strongestDelta ? `${strongestDelta.contactName} currently has the strongest pricing variance.` : 'Add a contracted price to compare commercial variance against the base rate.'}
          </p>
        </div>
      </div>

      {error && <div className="text-sm text-red-600 mb-2">{error}</div>}

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-gray-500">No customer-specific pricing configured.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase text-gray-600 dark:text-gray-400">
              <tr>
                <th className="text-left p-2">Customer</th>
                <th className="text-left p-2">Customer SKU</th>
                <th className="text-right p-2">Contracted Price</th>
                <th className="text-right p-2">vs Base</th>
                <th className="text-right p-2">Min Qty</th>
                <th className="text-left p-2">Window</th>
                <th className="text-center p-2">Active</th>
                <th className="text-right p-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const delta = computeDelta(it.contracted_price);
                const isEditing = editingId === it.id;
                return (
                  <tr key={it.id} className="border-t border-gray-200 dark:border-gray-700">
                    <td className="p-2 text-gray-900 dark:text-white">
                      <div>
                        <div className="font-medium">{it.contact_name || `Contact #${it.contact_id}`}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          {it.is_preferred && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                              <Star className="h-3 w-3" /> Preferred
                            </span>
                          )}
                          {!it.is_active && (
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                              Inactive
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-2 text-gray-600 dark:text-gray-400 font-mono text-xs">
                      {it.customer_sku || '—'}
                    </td>
                    <td className="p-2 text-right">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={draft.contracted_price}
                          onChange={(e) => setDraft((d) => ({ ...d, contracted_price: e.target.value }))}
                          className="w-24 px-2 py-1 border rounded text-right font-mono text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                        />
                      ) : (
                        <span className="font-mono">
                          {formatMoney(it.contracted_price, it.contract_currency || baseCurrency)}
                        </span>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      {delta.pct === null ? (
                        '—'
                      ) : (
                        <span
                          className={`font-mono text-xs ${delta.better ? 'text-emerald-600 dark:text-emerald-400' : 'text-orange-600 dark:text-orange-400'
                            }`}
                        >
                          {delta.pct > 0 ? '+' : ''}
                          {delta.pct.toFixed(1)}%
                        </span>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={draft.min_order_qty}
                          onChange={(e) => setDraft((d) => ({ ...d, min_order_qty: e.target.value }))}
                          className="w-20 px-2 py-1 border rounded text-right font-mono text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                        />
                      ) : (
                        <span className="font-mono">{it.min_order_qty ?? '—'}</span>
                      )}
                    </td>
                    <td className="p-2 text-xs text-gray-500">
                      {it.contract_start || it.contract_end
                        ? `${it.contract_start ? new Date(it.contract_start).toLocaleDateString() : '—'} → ${it.contract_end ? new Date(it.contract_end).toLocaleDateString() : '—'
                        }`
                        : '—'}
                    </td>
                    <td className="p-2 text-center">
                      <button
                        type="button"
                        onClick={() => toggleActive(it.id, it.is_active)}
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${it.is_active
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                          }`}
                      >
                        {it.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="p-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        {isEditing ? (
                          <>
                            <button
                              type="button"
                              onClick={() => commitEdit(it.id)}
                              className="text-emerald-600 hover:text-emerald-800"
                              title="Save"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingId(null)}
                              className="text-gray-500 hover:text-gray-700"
                              title="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => togglePreferred(it.id, it.is_preferred)}
                              className={`${it.is_preferred ? 'text-amber-500 hover:text-amber-600' : 'text-gray-400 hover:text-amber-500'}`}
                              title={it.is_preferred ? 'Remove preferred marker' : 'Mark as preferred'}
                            >
                              <Star className={`w-4 h-4 ${it.is_preferred ? 'fill-current' : ''}`} />
                            </button>
                            <button
                              type="button"
                              onClick={() => beginEdit(it.id, it.contracted_price, it.min_order_qty)}
                              className="text-blue-600 hover:text-blue-800 text-xs underline"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                if ((await confirm({ message: `Remove pricing for ${it.contact_name || `contact #${it.contact_id}`}?`, variant: 'danger' }))) {
                                  remove(it.id);
                                }
                              }}
                              className="text-red-600 hover:text-red-800"
                              title="Remove"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="mt-4 p-4 border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm text-gray-900 dark:text-white">Add Customer Pricing</h4>
            <button type="button" onClick={resetForm} className="text-gray-500 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>

          {formError && <div className="text-sm text-red-600">{formError}</div>}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Customer <span className="text-red-500">*</span>
              </label>
              <select
                value={form.contact_id}
                onChange={(e) => setForm((f) => ({ ...f, contact_id: Number(e.target.value) }))}
                required
                className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              >
                <option value={0}>Select a customer…</option>
                {availableContacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.currency && c.currency !== baseCurrency ? ` (${c.currency})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Currency</label>
              <input
                type="text"
                value={form.contract_currency || baseCurrency}
                onChange={(e) => setForm((f) => ({ ...f, contract_currency: e.target.value.toUpperCase().slice(0, 3) }))}
                maxLength={3}
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono uppercase bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Contracted Price
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form._contracted_price_str}
                onChange={(e) => setForm((f) => ({ ...f, _contracted_price_str: e.target.value }))}
                placeholder={baseRefValid ? String(baseRef.toFixed(2)) : '0.00'}
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono text-right bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Min Order Qty</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.min_order_qty ?? ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, min_order_qty: e.target.value === '' ? null : Number(e.target.value) }))
                }
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono text-right bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Customer SKU</label>
              <input
                type="text"
                value={form.customer_sku || ''}
                onChange={(e) => setForm((f) => ({ ...f, customer_sku: e.target.value }))}
                placeholder="Their part number"
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Contract Start</label>
              <input
                type="date"
                value={form.contract_start || ''}
                onChange={(e) => setForm((f) => ({ ...f, contract_start: e.target.value || null }))}
                className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Contract End</label>
              <input
                type="date"
                value={form.contract_end || ''}
                onChange={(e) => setForm((f) => ({ ...f, contract_end: e.target.value || null }))}
                className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Lead Time (days)</label>
              <input
                type="number"
                min="0"
                value={form.lead_time_days ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, lead_time_days: e.target.value === '' ? null : Number(e.target.value) }))}
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono text-right bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
              <input
                type="text"
                value={form.notes || ''}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Optional"
                className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>

            <div className="md:col-span-3 flex flex-wrap gap-4 rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={!!form.is_preferred}
                  onChange={(e) => setForm((f) => ({ ...f, is_preferred: e.target.checked }))}
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                Mark this contract as preferred
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={form.is_active !== false}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                Contract is active immediately
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={resetForm}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                void handleCreate(e);
              }}
              disabled={submitting || !form.contact_id}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
            >
              <Save className="w-4 h-4" /> {submitting ? 'Saving…' : 'Save Pricing'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
